import { Helmet, HelmetData } from "react-helmet-async";
import {
  AI_BG_NEW,
  AI_Core_Model_Img,
  Core_Alpha,
  Core_Heavy,
  Core_Lite_Icon,
  PLLM_Hero_Img,
} from "../../assets";
import { useModal } from "../../utils/demoProvider";
import { Link } from "react-router-dom";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../utils/animations";
const helmetData = new HelmetData({});

const why = [
  {
    icon: Core_Lite_Icon,
    title: "CORE Lite",
    subtitle: "Fast, efficient, high throughput.",
    text: "Handles high-volume, repeatable analytics for dashboards, regulatory reporting, monitoring, and operational workflows. ",
  },
  {
    icon: Core_Heavy,
    title: "CORE Heavy",
    subtitle: "Complex, multi-step analytical reasoning.",
    text: "Performs deeper causal analysis, helping teams understand the why behind revenue movement, operational anomalies, risk exposure, and fraud patterns."
  },
  {
    icon: Core_Alpha,
    title: "CORE Alpha",
    subtitle: "Quantitative finance specialist",
    text: "Built for quantitative finance, powering institutional backtesting, trade surveillance, and market intelligence with auditable reasoning chains that compliance teams can interrogate.",
  },
];

export default function Core() {
  const { openModal } = useModal();
  const pageRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        maskedLineReveal(pageRef.current.querySelector(".pllm-heading"), {
          scrollTrigger: revealTrigger(".pllm-heading"),
        });
        riseFrom(".pllm-hero-item", {
          delay: 0.2,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".pllm-hero-item", "top 85%"),
        });
        riseFrom(".pllm-hero-media", {
          y: 40,
          duration: 1.1,
          delay: 0.3,
          scrollTrigger: revealTrigger(".pllm-hero-media", "top 85%"),
        });
        riseFrom(".pllm-why-heading", {
          scrollTrigger: revealTrigger(".pllm-why-heading"),
        });
        riseFrom(".pllm-why-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".pllm-why-row", "top 75%"),
        });
        riseFrom(".pllm-why-copy > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".pllm-why-row", "top 75%"),
        });
        riseFrom(".pllm-family-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".pllm-family-head"),
        });
        riseFrom(".pllm-family-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".pllm-family-grid", "top 75%"),
        });
        riseFrom(".pllm-cta", {
          stagger: 0.08,
          scrollTrigger: revealTrigger(".pllm-cta", "top 90%"),
        });
      });
      return () => mm.revert();
    },
    { scope: pageRef }
  );

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>
          CORE — Small Language Models Built for Analytics Reasoning
        </title>
        <link rel="canonical" href="https://analyticsintelligence.com/core" />
        <meta
          name="description"
          content="CORE by Analytics Intelligence is a family of fine-tuned small language models with chain-of-thought reasoning — CORE Lite, Heavy, and Alpha, purpose-built for analytics interpretation and quantitative finance."
        />
        <meta
          name="keywords"
          content="CORE, small language models, SLM, chain-of-thought reasoning, analytics reasoning models, CORE Lite, CORE Heavy, CORE Alpha, quantitative finance AI, fine-tuned language models, explainable AI, Analytics Intelligence"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta
          property="og:title"
          content="CORE — Small Language Models Built for Analytics Reasoning"
        />
        <meta
          property="og:description"
          content="A family of fine-tuned small language models with chain-of-thought reasoning — CORE Lite, Heavy, and Alpha, built for analytics interpretation and quantitative finance."
        />
        <meta
          property="og:url"
          content="https://analyticsintelligence.com/core"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta
          name="twitter:title"
          content="CORE — Small Language Models Built for Analytics Reasoning"
        />
        <meta
          name="twitter:description"
          content="A family of fine-tuned small language models with chain-of-thought reasoning — CORE Lite, Heavy, and Alpha, built for analytics interpretation and quantitative finance."
        />
        <meta
          name="twitter:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "CORE — Analytics Reasoning Language Models",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web, API",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            },
            "description": "CORE is a family of fine-tuned small language models with chain-of-thought reasoning by Analytics Intelligence, purpose-built for analytics interpretation, actionable insight generation, and quantitative finance. The family includes CORE Lite (fast, high-throughput analytics), CORE Heavy (complex multi-step reasoning), and CORE Alpha (quantitative finance specialist).",
            "url": "https://analyticsintelligence.com/core",
            "featureList": ["Chain-of-thought reasoning", "Fine-tuned small language models", "CORE Lite", "CORE Heavy", "CORE Alpha", "Auditable reasoning chains"]
          }
        `}</script>
      </Helmet>

      <main ref={pageRef}>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[95vh] flex items-center"
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16  pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-4 flex flex-col items-center md:items-start">
                <span className="pllm-hero-item text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  CORE
                </span>
                <h2
                  className="pllm-heading text-[36px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white"
                  dangerouslySetInnerHTML={{
                    __html: "AI that doesn't just answer, it reasons",
                  }}
                />
                <p className="pllm-hero-item text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                  CORE is a family of fine-tuned small language models with
                  chain-of-thought reasoning, purpose-built for analytics
                  interpretation, actionable insight generation, and
                  quantitative finance.
                </p>
              </div>

              <div className="pllm-hero-item flex flex-row justify-center md:justify-start gap-3 sm:gap-4 w-full">
                <button
                  onClick={openModal}
                  className="text-sm md:text-base font-semibold py-3 min-w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2"
                >
                  Explore the API
                </button>
              </div>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img
                className="pllm-hero-media w-[450px]"
                src={PLLM_Hero_Img}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section className="bg-[#010127] relative flex flex-col pb-[96px]">
          <div className="w-[150px] lg:w-[400px] h-[200px] lg:h-[400px] bg-[#0073FF]/40 blur-[150px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>
          <h2 className="pllm-why-heading text-white font-extrabold text-[24px] md:text-[32px] leading-[1.2] text-center mb-11">
            Why Small, Specialised Models Win
          </h2>
          <div className="pllm-why-row container flex flex-col-reverse md:flex-row items-center gap-8 md:gap-4 lg:gap-2 xl:gap-0 z-[100]">
            <div className="flex-1 basis-2/4 flex items-center justify-center">
              <img
                src={AI_Core_Model_Img}
                alt="Data Structures"
                className="pllm-why-media w-full md:max-w-[480px] h-auto"
              />
            </div>

            <div className="pllm-why-copy flex-1 basis-2/4 space-y-4">
              <h2 class="text-2xl md:text-[32px] font-extrabold text-white text-center md:text-left leading-[1.2] md:leading-[40px]">
                Smaller. Faster. Cheaper. More accurate, for the work that
                matters.
              </h2>
              <p className="text-white text-sm md:text-base">
                General-purpose LLMs struggle with deep analytics, they’re
                costly, slow, and often imprecise. CORE models, fine-tuned for
                analytical and financial reasoning, deliver fast,
                cost-efficient, step-by-step insights you can trust and audit.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-[#EDF1FF] py-[48px]">
          <div className="container space-y-12">
            <div className="pllm-family-head">
              <h2 className="text-[#020128] font-extrabold text-[24px] md:text-[32px] text-center">
                The CORE Family
              </h2>
              <p className="text-sm md:text-base text-center">
                Three models. Each built for a different depth of reasoning
              </p>
            </div>

            <div className="pllm-family-grid grid grid-cols-12 gap-y-6 md:gap-y-8 md:gap-11">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className={`pllm-family-card group bg-white hover:bg-poppingBlue flex flex-col md:flex-row items-center md:items-start rounded-xl px-3 py-6 gap-6 transition-colors duration-700 ${idx === 2 ? "col-span-12 lg:col-start-4 lg:col-end-10" : "col-span-12 lg:col-span-6"}`}
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16 transition-all duration-700 group-hover:brightness-0 group-hover:invert"
                  />
                  <div className="space-y-1">
                    <h4 className="text-center md:text-left text-lg lg:text-xl xl:text-2xl font-bold text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.title}
                    </h4>

                    <h5 className="text-center md:text-left text-[#0073FF] text-base md:text-lg font-bold transition-colors duration-700 group-hover:text-white">
                      {item.subtitle}
                    </h5>

                    <p className="text-center md:text-left text-sm md:text-base text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pllm-cta flex justify-center items-center gap-x-3 md:gap-x-8">
              <Link to="/contact">
                <button className="text-sm md:text-base font-semibold py-3 px-4 md:px-6 min-w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                  Talk to sales
                </button>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
